import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'
import closest from 'closest'
import moment from 'moment'

import { withTheme, IWithThemeProps } from '../../theme'
import { IInputProps, IFormFieldMeta } from '../../types'

import { Datepicker } from '../datepicker/datepicker'
import { Popup } from '../popup'

import DateIcon from './date.svg'
import textFieldShape from './text-field-shape'
import getStyles from './styles'

/* eslint-disable jsx-a11y/no-static-element-interactions */

/**
 * TextField
 *
 * Component for text input
 *
 * @type React.Component
 *
 * @example

  import { TextField } from '../../ui-kit'

   <TextField type='text' placeholder='My placeholder' value='My fancy value' />

 */

const submitFormOnKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement>,
  _onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
) => {
  if (typeof _onKeyDown === 'function') _onKeyDown(event)

  if (event.isDefaultPrevented()) return

  // CMD + Enter
  if ((event.metaKey || event.ctrlKey) && event.keyCode === 13) {
    const form = closest(event.currentTarget, 'form')
    if (form) form.dispatchEvent(new Event('submit'))
  }
}

type TextFieldVariant = 'normal' | 'success'

const variants: TextFieldVariant[] = ['normal', 'success']

export interface ITextFieldProps {
  type?: string
  error?: React.ReactNode | boolean
  variant?: TextFieldVariant
  readOnly?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  flush?: boolean
  hasErrored?: boolean
  autoComplete?: string
  min?: string
  max?: string
  placeholder?: string

  meta?: IFormFieldMeta

  style?: Rule
  wrapperStyle?: Rule
  multiline?: boolean
  autoFocus?: boolean
}

export const TextField = withTheme(
  class TextField extends React.Component<
    ITextFieldProps & { input?: IInputProps<React.ChangeEvent<HTMLInputElement>> } & IInputProps<
        React.ChangeEvent<HTMLInputElement>
      > &
      IWithThemeProps
  > {
    static propTypes = {
      ...textFieldShape,
      style: PropTypes.object,
      multiline: PropTypes.bool,
      onKeyDown: PropTypes.func,
      wrapperStyle: PropTypes.object,
      variant: PropTypes.oneOf(variants),
      meta: PropTypes.object,
      theme: PropTypes.object.isRequired,
      autoFocus: PropTypes.bool,
    } as any

    // Set the default props even though we have default arguments in the TextField
    // stateless component's function declaration to allow UI Storybooks to access
    // this value
    static defaultProps = {
      variant: 'normal' as TextFieldVariant,
      wrapperStyle: {},
      meta: {},
      autoFocus: false,
    }

    constructor(
      props: ITextFieldProps & { input?: IInputProps<React.ChangeEvent<HTMLInputElement>> } & IInputProps<
          React.ChangeEvent<HTMLInputElement>
        > &
        IWithThemeProps
    ) {
      super(props)

      this.input = React.createRef()
    }

    handleScroll = (e: Event) => e.preventDefault()

    handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      const { input, onFocus } = this.props
      if (onFocus) onFocus(e)
      if (input && input.onFocus) input.onFocus(e)
      if (this.input.current) this.input.current.addEventListener('mousewheel', this.handleScroll)
    }

    handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const { input, onBlur } = this.props
      if (onBlur) onBlur(e)
      if (input && input.onBlur) input.onBlur(e)
      if (this.input.current) this.input.current.removeEventListener('mousewheel', this.handleScroll)
    }

    handleDateChange = (value: moment.Moment) => {
      const inputProps = this.getInputProps()
      if (inputProps.onChange)
        inputProps.onChange({
          target: { value },
          currentTarget: { value },
        } as any) // SMELL, using any due to lazy
    }

    getInputProps() {
      const {
        variant,
        readOnly,
        disabled,
        onKeyDown,
        type,
        value,
        multiline,
        icon,
        style,
        input,
        flush,
        wrapperStyle,
        meta,
        hasErrored,
        ...additionalProps
      } = this.props
      const hasError = this.hasErrored()

      const styles = getStyles(this.props.theme)

      return {
        readOnly,
        disabled,
        type: type === 'date' ? ' text' : type,
        value,
        ...input,
        ...additionalProps,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        onKeyDown,
        ...css([
          styles.input.base,
          !readOnly && !hasError && variant === 'normal' && styles.input.normal,
          variant === 'success' && !readOnly && styles.input.success,
          hasError && styles.input.error,
          readOnly && styles.input.readOnly,
          disabled && styles.input.disabled,
          icon && styles.input.withIcon,
          type === 'password' && value && styles.input.password,
          multiline && styles.input.multiline,
          window.navigator.userAgent &&
            window.navigator.userAgent.includes('iPhone') && {
              fontSize: '16px',
            },
          style,
        ]),
      }
    }

    static VARIANTS = variants

    input: React.RefObject<HTMLInputElement>

    hasErrored() {
      const { meta, hasErrored } = this.props
      return hasErrored || (meta && meta.error && meta.touched && !meta.active && meta.dirty)
    }

    renderDate(inputProps: IInputProps<React.ChangeEvent<HTMLInputElement>>) {
      const { type, min, max, icon, disabled, readOnly, theme } = this.props

      return (
        type === 'date' && (
          <Popup
            renderPopup={({ toggleOpen }) => (
              <Datepicker
                firstSelectableDate={min != null ? moment(min) : undefined}
                lastSelectableDate={max != null ? moment(max) : undefined}
                onChange={val => {
                  this.handleDateChange(val)
                  toggleOpen()
                }}
                style={{
                  marginLeft: theme.settings.spacing.large,
                  marginRight: theme.settings.spacing.large,
                  marginTop: theme.settings.spacing.large,
                }}
                value={inputProps.value}
              />
            )}
            renderToggle={({ toggleOpen, ref }) => (
              <span
                onClick={disabled || readOnly ? undefined : toggleOpen}
                onKeyPress={
                  disabled || readOnly ? undefined : e => (e.key === 'Enter' || e.key === ' ') && toggleOpen(e)
                }
                ref={ref}
                role={disabled || readOnly ? undefined : 'button'}
                tabIndex={disabled || readOnly ? undefined : 0}
                {...css({
                  position: 'absolute',
                  top: '50%',
                  right: theme.settings.spacing.small,
                  transform: 'translateY(-50%)',
                  cursor: disabled || readOnly ? 'default' : 'pointer',
                  opacity: disabled || readOnly ? '0.5' : '1',
                  '.intent-mouse &:focus': {
                    outline: 'none',
                  },
                })}
              >
                {icon || <DateIcon fill={theme.colors.ui.medium} />}
              </span>
            )}
          />
        )
      )
    }

    renderIcon() {
      const styles = getStyles(this.props.theme)

      return (
        this.props.type !== 'date' &&
        this.props.icon && (
          <span {...css([styles.icon.base, this.props.multiline && styles.icon.multiline])}>{this.props.icon}</span>
        )
      )
    }

    render() {
      const { onKeyDown, multiline, style, flush, wrapperStyle, autoFocus, onClick } = this.props
      const inputProps = this.getInputProps()

      const styles = getStyles(this.props.theme)

      const wrapperStyles = [
        styles.wrapper.base,
        flush && styles.wrapper.flush,
        multiline && styles.wrapper.multiline,
        style && style.height && { height: style.height },
        style && style.maxHeight && { maxHeight: style.maxHeight },
        style && style.width && { width: style.width },
        style && style.maxWidth && { maxWidth: style.maxWidth },
        wrapperStyle,
        {
          '& input, & textarea': {
            '::placeholder': {
              color: '#9FA6AC',
              fontStyle: 'italic',
            },
          },
        },
      ]

      const inputField = multiline
        ? React.createElement('textarea', {
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => submitFormOnKeyDown(event, onKeyDown),
            ...inputProps,
          })
        : React.createElement('input', {
            type: 'text',
            ref: this.input,
            autoFocus,
            ...inputProps,
          })

      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div {...css(wrapperStyles)} onClick={onClick}>
          {inputField}
          {this.renderDate(inputProps)}
          {this.renderIcon()}
        </div>
      )
    }
  }
)
